import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { API_VERSION_PREFIX, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";

/**
 * Freelancer skills, read only (i92).
 *
 * Skill tags need no approval at launch: a freelancer can only pick from HUDDL's
 * controlled vocabulary, so the vocabulary is the control. This page is how
 * staff SEE what is in use: every skill, who holds it, and how many live
 * products each holder sells under it. There is nothing to approve or edit here
 * by design; a skill is retired by HUDDL in the database, and a business is
 * dealt with from its own Business Center record.
 */

interface SkillUsageDto {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: "active" | "retired";
  holder_count: number;
  holders: {
    business_id: string;
    public_id: string;
    display_name: string;
    slug: string | null;
    status: string;
    visibility_status: string;
    live_products: number;
  }[];
}

export function FreelancerSkillsPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Business Center", to: "/app/businesses" },
    { label: "Gig skills" },
  ]);

  const client = useApiClient();
  const navigate = useNavigate();
  const [skills, setSkills] = useState<SkillUsageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<{ skills: SkillUsageDto[] }>(`${API_VERSION_PREFIX}/hq/freelancer-skills`);
      setSkills(res.skills);
    } catch (err) {
      setError(apiErrorMessage(err));
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalHolders = useMemo(() => new Set(skills.flatMap((s) => s.holders.map((h) => h.business_id))).size, [skills]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Gig skills"
        description={`The controlled vocabulary gigs pick their skill tags from, and who holds each. Tags need no approval. ${totalHolders} gig${totalHolders === 1 ? "" : "s"} hold at least one.`}
        actions={
          <Button variant="secondary" onClick={() => void navigate("/app/businesses")}>
            Back to Businesses
          </Button>
        }
      />
      {error ? (
        <Alert tone="danger" title="Unable to load gig skills">
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message="Loading gig skills…" /> : null}
      {!loading && !error && skills.length === 0 ? (
        <EmptyState
          title="No skills defined"
          description="The vocabulary is reference data. Run the i92 migration on this project to create it."
          reason="none"
        />
      ) : null}
      {!loading && skills.length > 0 ? (
        <Table>
          <Thead>
            <Tr>
              <Th>Skill</Th>
              <Th>Status</Th>
              <Th>Gigs</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {skills.map((skill) => (
              <SkillRows
                key={skill.id}
                skill={skill}
                expanded={open === skill.id}
                onToggle={() => setOpen(open === skill.id ? null : skill.id)}
                onOpenBusiness={(id) => void navigate(`/app/businesses/${id}`)}
              />
            ))}
          </Tbody>
        </Table>
      ) : null}
    </Stack>
  );
}

function SkillRows({
  skill,
  expanded,
  onToggle,
  onOpenBusiness,
}: {
  skill: SkillUsageDto;
  expanded: boolean;
  onToggle: () => void;
  onOpenBusiness: (businessId: string) => void;
}) {
  return (
    <>
      <Tr>
        <Td>
          <Text role="bodyStrong" as="div">
            {skill.name}
          </Text>
          {skill.description ? <Text role="caption">{skill.description}</Text> : null}
        </Td>
        <Td>
          <StatusChip tone={skill.status === "active" ? "success" : "neutral"}>
            {skill.status === "active" ? "Active" : "Retired"}
          </StatusChip>
        </Td>
        <Td>{skill.holder_count}</Td>
        <Td>
          <Button size="sm" variant="secondary" onClick={onToggle} disabled={skill.holder_count === 0}>
            {expanded ? "Hide" : "Show"}
          </Button>
        </Td>
      </Tr>
      {expanded
        ? skill.holders.map((h) => (
            <Tr key={`${skill.id}:${h.business_id}`}>
              <Td>
                <Flex gap={8}>
                  <Text as="span">{h.display_name}</Text>
                  {h.slug ? <Text role="caption">/{h.slug}</Text> : null}
                </Flex>
              </Td>
              <Td>
                <StatusChip tone={h.status === "active" && h.visibility_status === "visible" ? "success" : "neutral"}>
                  {h.status === "active" ? (h.visibility_status === "visible" ? "Public" : "Hidden") : h.status}
                </StatusChip>
              </Td>
              <Td>{h.live_products} live {h.live_products === 1 ? "product" : "products"}</Td>
              <Td>
                <Button size="sm" variant="primary" onClick={() => onOpenBusiness(h.business_id)}>
                  Open
                </Button>
              </Td>
            </Tr>
          ))
        : null}
    </>
  );
}
